import type { StepperIndicatorProps } from './stepperIndicatorTypes'
import type { StepperIndicatorVariant } from './stepperIndicatorTypes'

import Box from '../box'
import { styled, th, useStyleConfig, useTheme, vui } from '../core'
import Icon from '../icon'
import T from '../t'
import { cs } from '../utils'
import { CSS_VARS } from './stepperConsts'
import { ICON_SIZES } from './stepperIndicatorTheme'

const IndicatorBox = styled.spanBox<{ $bg: string; $border: string; $color: string }>`
  align-items: center;
  background-color: var(${CSS_VARS.indicatorBg}, ${p => p.$bg});
  border-color: var(${CSS_VARS.indicatorBorder}, ${p => p.$border});
  border-radius: 50%;
  border-style: solid;
  border-width: 1px;
  color: var(${CSS_VARS.indicatorColor}, ${p => p.$color});
  display: inline-flex;
  justify-content: center;
`

const NumberIndicatorBox = styled(IndicatorBox)`
  outline: var(${CSS_VARS.indicatorOutline}, 6px solid white);
`

type ContentProps = { number?: number; variant?: StepperIndicatorVariant; size?: 'md' | 'sm' }

const Content = ({ number, variant, size }: ContentProps) => {
  if (typeof number === 'undefined') return null
  const statusIconSize = ICON_SIZES.status[size ?? 'md']
  if (variant === 'completed') return <Icon name="uiCheck" size={statusIconSize} />
  if (variant === 'error') return <Icon name="uiTimes" size={statusIconSize} />

  return (
    <T fontSize={size === 'md' ? 16 : 14} fontWeight={600}>
      {number}
    </T>
  )
}

/**
 * Renders a step indicator circle with a number, icon, or status variant.
 * Sub-component of the Stepper namespace.
 */
export const StepperIndicator = vui<'div', StepperIndicatorProps>((props, ref) => {
  const { icon, number, className, size = 'md', variant = 'active', ...rest } = props

  const styles = useStyleConfig('StepperIndicator', props)
  const { circle: circleStyles, icon: iconStyles, height, width } = styles
  const isIconMode = Boolean(icon)
  const slot = isIconMode ? iconStyles : circleStyles
  const theme = useTheme()
  const rc = (token: string) => th.color(token)({ theme }) ?? token
  const indicatorColors = {
    $bg: rc(slot?.bg ?? 'transparent'),
    $border: rc(slot?.borderColor ?? 'transparent'),
    $color: rc(slot?.color ?? 'inherit'),
  }

  const indicatorClassName = cs(
    'vui-stepperIndicator',
    isIconMode ? 'vui-stepperIndicatorIcon' : 'vui-stepperIndicatorNumber',
    className,
  )

  const outline = isIconMode ? undefined : '6px solid white'
  const Root = isIconMode ? IndicatorBox : NumberIndicatorBox
  const content = isIconMode ? (
    <Icon name={icon!} size={ICON_SIZES.iconMode[size]} />
  ) : (
    <Content number={number} size={size} variant={variant} />
  )

  return (
    <Root
      aria-hidden="true"
      className={indicatorClassName}
      ref={ref}
      {...indicatorColors}
      {...rest}
      h={height as string}
      w={width as string}
    >
      {content}
    </Root>
  )
})

StepperIndicator.displayName = 'StepperIndicator'

export default StepperIndicator
